import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import axios, { AxiosHeaders } from 'axios';
import { CustomButton } from 'components/roundedbutton/CustomButton';
import { useSecretSequence } from 'hooks/useSecretSequence';
import { useSnackbarCustom } from 'hooks/useSnackCustom';
import { IToken } from 'model/token.model';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setCaixa } from 'redux/features/caixaSlice';
import { setEsSelecionado } from 'redux/features/esSelecionadoSlice';
import { setToken } from 'redux/features/tokenSlice';
import { useAppDispatch } from 'redux/hooks';
import { ROLES } from 'roles/roles';
import StatusServicoService from 'service/status-servico.service';
import { paletaPadrao, theme } from 'theme';
import { toNexus } from '../../../src/axios/axios.config';
import LoginSohQBom from './LoginSohQBom';
import LoginService from './login.service';


const inputLabelProps = {
    sx: {
        fontWeight: 'bold',
        color: theme.palette.primary.main
    },
};

const inputProps = {
    sx: {
        '&:before': {
            borderBottomColor: theme.palette.primary.main,
        },
    },
    spellCheck: false
};

const Login = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { addError } = useSnackbarCustom();

    const loginService = LoginService();
    const statusServicoService = StatusServicoService();
    const userRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(false);
    const [login, setLogin] = useState({
        user: '',
        password: '',
    });

    const [currentVersion, setCurrentVersion] = useState('');

    useEffect(() => {
        userRef.current?.focus();
        statusServicoService.getCurrentVersion()
            .then(({ data }) => {
                return setCurrentVersion('v' + data.replace('-SNAPSHOT', ''));
            })
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const appDispatch = useAppDispatch();

    const getSaldoCaixa = (token: IToken) => {
        const headers = new AxiosHeaders();
        headers.set('access_token', token.access_token);
        headers.set('es', token.ESTABELECIMENTOS[0].uuid);

        const baseURL = `${toNexus(window.location.host, window.location.hostname)}`;

        axios.get(`${baseURL}/flow/caixadisponivel`, { headers: headers })
            .then(({ data }) => {
                const caixa = data.find((e: any) => e.caixaAberto && e.operadorAtual === token.USER_INFO?.username);
                if (caixa) {
                    axios.get(`${baseURL}/flow/saldocaixa/${caixa.uuid}`, { headers: headers })
                        .then(({ data }) => {
                            appDispatch(setCaixa({
                                caixa: data.caixa,
                                contaSelecionada: null,
                                saldo: data.saldo,
                                dataAbertura: data.dataAbertura,
                                caixaAberto: data.caixaAberto
                            }));
                            navigate('/registra-venda');
                        })
                        .catch(() => {
                            navigate('/caixa');
                        });
                } else {
                    navigate('/caixa');
                }
            })
            .catch(error => {
                if (error.response?.data?.code === "ProibidoException") {
                    addError({
                        message: 'Não foi possível fazer login. Tente novamente.',
                        closeable: true,
                        preventDuplicate: false,
                    });
                }
                navigate('/caixa');
            });
    };

    const onSubmitLogin = (e: any) => {
        e.preventDefault();
        if (!login.password.length || !login.user.length) {
            return addError({
                message: 'Preencha todos os campos corretamente!',
                id: 'preenche-sah-coisa-bonita',
                closeable: true,
                preventDuplicate: true,
            });
        }

        setShowPassword(false);
        setLoading(true);
        loginService.loga(login)
            .then(({ data }) => {
                dispatch(setToken(data));
                dispatch(setEsSelecionado(data.ESTABELECIMENTOS[0]));

                const hasCaixaRole =
                    data.KEY_CLOAK_ROLES.includes(ROLES.ADMIN) ||
                    data.KEY_CLOAK_ROLES.includes(ROLES.SUPER) ||
                    data.KEY_CLOAK_ROLES_SUBS.includes(ROLES.CAIXA);

                if (hasCaixaRole) {
                    getSaldoCaixa(data);
                } else {
                    navigate('/home');
                }
            })
            .catch(error => {
                addError({
                    message: 'Não foi possível fazer login. Tente novamente.',
                    closeable: true,
                    preventDuplicate: false,
                });
            })
            .finally(() => {
                setLoading(false);
                setTimeout(() => {
                    userRef.current?.focus();
                },);
            });
    };

    const onChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        setLogin({ ...login, [target.name]: target.value });
    };

    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        if (!loading) {
            setShowPassword(!showPassword);
        }
    };

    const [secret, setSecret] = useState(false);

    useSecretSequence([
        'ArrowUp',
        'ArrowUp',
        'ArrowDown',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'ArrowLeft',
        'ArrowRight',
        'b',
        'a',
        'Enter'
    ], () => {
        setSecret(true);
        setTimeout(() => {
            userRef.current?.focus();
        }, 100);
    });

    return <>

        {!secret && <Box
            sx={{
                height: '100vh',
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
            }}
        >
            <Box
                id='imgWrapper'
                sx={{
                    width: '50%',
                    height: '100%',
                    backgroundImage: "url('/assets/bg2.png')",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    display: 'grid',
                    placeItems: 'center',
                    userSelect: 'none',
                }}
            >
                <img src='assets/logoFlow.svg' width='300px' alt='logoFlow' />
            </Box>

            <Box
                id='contentWrapper'
                sx={{
                    width: '50%',
                    background: theme.palette.background.default,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                }}
            >
                <form
                    onSubmit={onSubmitLogin}
                    style={{
                        width: '50%',
                        maxWidth: '260px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '35px'
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px'
                    }}>
                        <img
                            id='logo'
                            src="logo512.png"
                            style={{
                                height: '2.3rem',
                                objectFit: 'contain'
                            }}
                            alt='flow-logo-icon-entrar'
                        />

                        <Typography
                            variant="h3"
                            fontWeight={'bold'}
                            width={'100%'}
                            fontSize='1.5rem'
                            textAlign='center'
                            color={paletaPadrao.verdeEscuro}
                        >
                            Bem-vindo!
                        </Typography>
                    </Box>

                    <TextField
                        id="input-user"
                        label="Nome de usuário"
                        name='user'
                        variant="standard"
                        color='primary'
                        fullWidth
                        value={login.user}
                        disabled={loading}
                        onChange={onChange}
                        inputRef={userRef}
                        autoComplete='username'
                        InputProps={inputProps}
                        InputLabelProps={inputLabelProps}
                    />

                    <TextField
                        id="input-password"
                        label="Senha"
                        name='password'
                        variant="standard"
                        color='primary'
                        fullWidth
                        value={login.password}
                        onChange={onChange}
                        disabled={loading}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete='current-password'
                        InputProps={inputProps}
                        InputLabelProps={inputLabelProps}
                    />

                    <CustomButton
                        variant="contained"
                        color='primary'
                        disabled={loading}
                        type='submit'
                        id='login-btn'
                        sx={{
                            marginTop: '10px',
                            width: '200px',
                            height: '30px',
                            borderRadius: '10px',
                            fontSize: '12.33px',
                            textTransform: 'capitalize',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading && <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                display: 'grid',
                                placeItems: 'center'
                            }}>
                            <CircularProgress size={16} />
                        </Box>}
                        Entrar
                    </CustomButton>

                </form>
            </Box>
        </Box>}

        {secret &&
            <LoginSohQBom
                loading={loading}
                onSubmitLogin={onSubmitLogin}
                login={login}
                userRef={userRef}
                showPassword={showPassword}
                togglePasswordVisibility={togglePasswordVisibility}
                onChange={onChange} />
        }


        <Typography
            sx={{
                color: theme.palette.text.primary,
                userSelect: 'none',
                fontSize: '12px',
                position: 'absolute',
                bottom: '0',
                right: { sx: '50%', sm: '5px' },
                transform: { sx: 'translateX(50%)', md: 'none' },
                zIndex: 1000,
            }}
        >
            {currentVersion}
        </Typography>
    </>;
};

export default Login;
